import type { SubscriptionStatusResponse } from "@/clients/models/SubscriptionStatusResponse";
import { SubscriptionsService } from "@/clients/services/SubscriptionsService";
import { QueryKeys } from "@/constants/query-keys";
import { QueryStaleTime } from "@/constants/query-stale-time";
import { reportSubscriptionInconsistencies } from "@/utils/subscription/subscription-status-monitoring";
import { resolveCancelableQuery } from "@/utils/tanstack-query/resolve-cancelable-query";
import { useUser } from "@clerk/clerk-react";
import { useIsMutating, useQuery } from "@tanstack/react-query";

interface UseGetSubscriptionStatusOptions {
  enabled?: boolean;
}

export const useGetSubscriptionStatus = (options?: UseGetSubscriptionStatusOptions) => {
  // Source the user id from Clerk's live session rather than the localStorage
  // mirror (USER_KEY). On iOS WebView localStorage gets evicted while the Clerk
  // session survives — reading USER_KEY there throws "User not found", the query
  // errors, and the paywall fails open. Clerk's id is available whenever signed
  // in, and the per-request `x-user-id` arg wins over the localStorage header.
  const { user, isLoaded } = useUser();
  const userId = user?.id;
  // OTP verification activates Clerk before creating the backend user. Keep
  // subscriptions paused until that mutation finishes, including its final
  // cache invalidation. Existing sessions still work without localStorage.
  const isRegistering = useIsMutating({ mutationKey: [QueryKeys.REGISTER] }) > 0;

  const query = useQuery({
    queryKey: [QueryKeys.SUBSCRIPTION_STATUS],
    queryFn: async ({ signal }): Promise<SubscriptionStatusResponse> => {
      // Defensive: `enabled` should prevent this, but never call the endpoint
      // with an empty x-user-id. Fail closed (throw) rather than send garbage.
      if (!userId) {
        throw new Error("Subscription status requested without a Clerk user id");
      }
      const response = await resolveCancelableQuery(
        SubscriptionsService.getSubscriptionStatusApiV1SubscriptionsStatusGet(userId),
        signal,
      );
      const { data } = response;
      // Surface backend payloads that contradict themselves (e.g. is_active
      // with a terminal status) — deduped, so this won't spam on refetch.
      reportSubscriptionInconsistencies(data);
      return data;
    },
    enabled: (options?.enabled ?? true) && isLoaded && Boolean(userId) && !isRegistering,
    staleTime: QueryStaleTime.SHORT,
    retry: (failureCount, error) => {
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status?: number }).status;
        // Identity / validation errors won't change on retry.
        if (status === 401 || status === 403 || status === 404 || status === 422) {
          return false;
        }
      }
      // Transient 5xx / network errors (which surface with no status) self-heal.
      return failureCount < 3;
    },
  });

  return {
    ...query,
    subscriptionStatus: query.data,
  };
};
